export interface SalesReturnItem {
  id?: string;
  sales_return_id?: string;
  variant_id: string;
  quantity: number;
  unit_price: number;
  discount_percent?: number;
  discount_amount?: number;
  taxable_amount?: number;
  tax_rate?: number;
  cgst_amount?: number;
  sgst_amount?: number;
  igst_amount?: number;
  tax_amount?: number;
  line_total?: number;
  description?: string;
  hsn_code?: string;
  created_at?: string;
  updated_at?: string;
  variant?: {
    id: string;
    name: string;
    sku: string;
    price: number;
    hsn_code?: string;
    product?: {
      id: string;
      name: string;
    };
  };
}

export enum SalesReturnStatus {
  DRAFT = 'draft',
  APPROVED = 'approved',
  PROCESSED = 'processed',
  REFUNDED = 'refunded',
  CLOSED = 'closed'
}

export enum SalesReturnReason {
  DEFECTIVE = 'defective',
  WRONG_ITEM = 'wrong_item',
  CUSTOMER_REQUEST = 'customer_request',
  QUALITY_ISSUES = 'quality_issues',
  OTHER = 'other'
}

export interface SalesReturn {
  id: string;
  return_number: string;
  invoice_id: string;
  customer_id: string;
  return_date: string;
  return_reason: string;
  return_description?: string;
  
  // Financial details
  subtotal: number;
  tax_amount: number;
  total_amount: number;
  refund_amount: number;
  
  // Status
  status: string;
  
  // Credit note information
  credit_note_number?: string;
  
  // Admin attribution
  created_by_admin_id: string;
  updated_by_admin_id?: string;
  status_changed_at?: string;
  status_changed_by_admin_id?: string;
  
  // Timestamps
  created_at: string;
  updated_at?: string;
  
  // Relationships
  items: SalesReturnItem[];
  customer?: {
    id: string;
    business_name?: string;
    contact_person?: string;
    email?: string;
    phone?: string;
  };
  invoice?: {
    id: string;
    invoice_number: string;
    invoice_date: string;
    total_amount: number;
    status: string;
  };
  created_by_admin?: {
    id: string;
    name: string;
    email: string;
  };
}

export interface SalesReturnCreate {
  invoice_id: string;
  return_date: string;
  return_reason: string;
  return_description?: string;
  items: Omit<SalesReturnItem, 'id' | 'sales_return_id' | 'created_at' | 'updated_at' | 'variant'>[];
}

export interface SalesReturnUpdate {
  return_date?: string;
  return_reason?: string;
  return_description?: string;
  items?: Omit<SalesReturnItem, 'id' | 'sales_return_id' | 'created_at' | 'updated_at' | 'variant'>[];
}

export interface SalesReturnStatusUpdate {
  status: string;
  notes?: string;
}

export interface SalesReturnSearchFilters {
  customer_id?: string;
  status?: string;
  return_reason?: string;
  return_date_from?: string;
  return_date_to?: string;
  search_term?: string;
  created_by_admin_id?: string;
  invoice_id?: string;
}

export interface SalesReturnListResponse {
  sales_returns: SalesReturn[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

// Status color mapping for consistent UI
export const SALES_RETURN_STATUS_COLORS: Record<SalesReturnStatus, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
  [SalesReturnStatus.DRAFT]: 'default',
  [SalesReturnStatus.APPROVED]: 'info',
  [SalesReturnStatus.PROCESSED]: 'warning',
  [SalesReturnStatus.REFUNDED]: 'success',
  [SalesReturnStatus.CLOSED]: 'success'
};

// Status labels for display
export const SALES_RETURN_STATUS_LABELS: Record<SalesReturnStatus, string> = {
  [SalesReturnStatus.DRAFT]: 'Draft',
  [SalesReturnStatus.APPROVED]: 'Approved',
  [SalesReturnStatus.PROCESSED]: 'Processed',
  [SalesReturnStatus.REFUNDED]: 'Refunded',
  [SalesReturnStatus.CLOSED]: 'Closed'
};

// Return reason color mapping
export const SALES_RETURN_REASON_COLORS: Record<SalesReturnReason, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
  [SalesReturnReason.DEFECTIVE]: 'error',
  [SalesReturnReason.WRONG_ITEM]: 'warning',
  [SalesReturnReason.CUSTOMER_REQUEST]: 'info',
  [SalesReturnReason.QUALITY_ISSUES]: 'error',
  [SalesReturnReason.OTHER]: 'default'
};

// Return reason labels for display
export const SALES_RETURN_REASON_LABELS: Record<SalesReturnReason, string> = {
  [SalesReturnReason.DEFECTIVE]: 'Defective',
  [SalesReturnReason.WRONG_ITEM]: 'Wrong Item',
  [SalesReturnReason.CUSTOMER_REQUEST]: 'Customer Request',
  [SalesReturnReason.QUALITY_ISSUES]: 'Quality Issues',
  [SalesReturnReason.OTHER]: 'Other'
};

export interface SalesReturnProcessRequest {
  refund_amount: number;
  credit_note_number?: string;
  notes?: string;
}

export interface SalesReturnProcessResponse {
  success: boolean;
  sales_return_id: string;
  return_number: string;
  credit_note_number?: string;
  refund_amount: number;
  message: string;
}